import React from "react";

/** Mobile-first "About Us" card
 * - Cleaner typography, better contrast & spacing
 * - Friendly icons + chips
 * - Fully responsive with accessible semantics
 * - Content preserved (typo in “platform” fixed for readability)
 */
const AboutCompany = () => {
  const rows = [
    {
      label: "Company Name",
      value: "BuildPro OGCS",
      iconBg: "bg-sky-100",
      iconEmoji: "🏢",
    },
    // { label: "Industry", value: "Civil Engineering Products", iconBg: "bg-rose-100", iconEmoji: "🏭" },
    {
      label: "Our Mission",
      value:
        "At BuildPro OGCS, we provide industry-leading manufacturers and innovative sellers the platform to grow their businesses, offering exposure to thousands of construction companies, contractors, and engineers across India.",
      iconBg: "bg-emerald-100",
      iconEmoji: "🎯",
    },
    {
      label: "Product Range",
      value:
        "Construction materials, heavy machinery, and other engineering solutions.",
      iconBg: "bg-amber-100",
      iconEmoji: "🧰",
    },
    {
      label: "Our Commitment",
      value:
        "Empower contractors, builders, and engineers by providing reliable and durable products.",
      iconBg: "bg-indigo-100",
      iconEmoji: "🤝",
    },
    {
      label: "Customer Support",
      value:
        "Our customer-first approach ensures we're always here to assist with product selection, bulk purchases, and order processing.",
      iconBg: "bg-fuchsia-100",
      iconEmoji: "📞",
    },
  ];

  return (
    <section
      className="px-4 sm:px-6 py-10 sm:py-12 bg-gradient-to-b from-sky-50 via-white to-sky-50"
      aria-labelledby="about-heading"
    >
      {/* Header */}
      <div className="max-w-4xl mx-auto text-center">
        <h2
          id="about-heading"
          className="text-2xl sm:text-3xl font-extrabold tracking-tight text-sky-900"
        >
          About Us
        </h2>
        <p className="mt-2 text-sky-700 text-sm sm:text-base">
          Quality materials. Reliable delivery. Expert support.
        </p>
      </div>

      {/* Card */}
      <div className="max-w-4xl mx-auto mt-6 sm:mt-8 bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-sky-100 overflow-hidden">
        {/* Top banner */}
        <div className="h-2 w-full bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600" />

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Feature chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            {["Trusted Supplier", "Bulk Orders", "Pan-India Reach", "Engineer Support"].map(
              (chip) => (
                <span
                  key={chip}
                  className="px-3 py-1 rounded-full text-[11px] sm:text-xs font-medium bg-sky-50 text-sky-800 border border-sky-100"
                >
                  {chip}
                </span>
              )
            )}
          </div>

          {/* Info rows */}
          <dl className="space-y-5">
            {rows.map(({ label, value, iconEmoji, iconBg }) => (
              <div
                key={label}
                className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 sm:p-4 bg-white hover:bg-sky-50/50 transition"
              >
                <div
                  className={`shrink-0 ${iconBg} text-sky-900 w-10 h-10 sm:w-11 sm:h-11 rounded-xl grid place-items-center`}
                  aria-hidden="true"
                >
                  <span className="text-lg sm:text-xl">{iconEmoji}</span>
                </div>
                <div className="min-w-0">
                  <dt className="text-sm sm:text-[15px] font-bold text-slate-900">
                    {label}
                  </dt>
                  <dd className="mt-1 text-slate-700 text-[13.5px] sm:text-sm leading-6">
                    {value}
                  </dd>
                </div>
              </div>
            ))}
          </dl>

          {/* CTA */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href="https://buildpro.ogcs.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400"
            >
              Visit Website
              <span aria-hidden>↗</span>
            </a>
            <a
              href="mailto:ogcspvtlt@gmail.com?subject=BuildPro%20Support&body=Hello%20OGCS%20Team%2C%20I%20need%20help%20with..."
              className="inline-flex justify-center items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-400"
            >
              Email Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer microcopy */}
      <p className="max-w-4xl mx-auto text-center mt-6 text-xs sm:text-[13px] text-slate-500">
        © {new Date().getFullYear()} BuildPro OGCS — Your partner for reliable construction materials.
      </p>
    </section>
  );
};

export default AboutCompany;
